import { endOfDay, getUnixTime, startOfDay } from "date-fns";

import type { components } from "#open-fetch-schemas/atlit";
import type { TSeasonType } from "~/common/constants/seasons";
import type { PLAYLIST_TYPE } from "~/constants";
import type {
  IAthleteHighlight,
  IBasicVideo,
  IClip,
  IEvent,
  IEventStatus,
  IGameHighlight,
  IMbeEvent,
  IResponseEvent,
  IResponseEvents,
  IResponseMBEEvent,
  IResponseSharedExternalEvent,
  ISharedExternalEvent,
  ITeam,
  ReplayEvent,
  TBreakdownType,
  ValuesOf,
} from "~/types";
import type { IPixellotPlayerSourceType } from "~~/modules/pixellot-sdk";
import { EVENT_STATUS, EVENT_TYPES } from "~/constants";
import formatTeam from "~/teams/helpers/format-team";
import formatEvent, { formatMBEEvent, formatSharedExternalEvent } from "~/videos/helpers/format-event";

import type { ISportAngle, TResponseSportAngle } from "../types/event-angles";
import { formatSportAngle } from "../helpers/format-angles";

export interface IRequestPaginateParams {
  limit: number;
  offset: number;
  sort?: "startDateTime" | "endDateTime";
}

export type TEventUrls = components["schemas"]["EventAttr"]["urls"];
export type TEventAngles = components["schemas"]["EventAttr"]["videosList"];
export type TExportAnglesPayload = components["schemas"]["ExportAnglesRequest"];

// temporary type until "vod" is added to the API status enum
export type TCreateEventPayload = Omit<components["schemas"]["EventAttr"], "status"> & {
  status: components["schemas"]["EventAttr"]["status"] | "vod";
  logging: {
    excludeFromStats: boolean;
    logAfterUpload: boolean;
    externalStatsLink?: string;
  };
  external?: {
    replay: number;
  };
  exchange?: boolean;
};

export interface IRequestEventParams extends IRequestPaginateParams {
  q: string;
  status: IEventStatus;
  clubId: string;
  teamId: string;
  orgId: string;
  leagueId: string;
  athleteId: string;
  startDate: number;
  endDate: number;
  sportType: string | string[];
  relationships: string;
  season: number | number[];
  type: string | string[];
  id: string;
  with: string;
  shared?: boolean;
  clubIdsShared?: string;
  playlistType?: ValuesOf<typeof PLAYLIST_TYPE>;
  include?: string;
}

export interface IRequestReplayEventsParams {
  clubId: string;
  season: number;
  seasonType: TSeasonType;
  name?: string;
  offset: number;
  limit: number;
}

export const getCachedEventsList = useMemoize(
  (query: Partial<IRequestEventParams>, leagueId?: string, teamId?: string): Promise<{ data: IEvent[]; total: number }> => {
    return getEventsList(query, leagueId, teamId);
  }
);

export async function getEventsList(query: Partial<IRequestEventParams>, leagueId?: string, teamId?: string): Promise<{ data: IEvent[]; total: number }> {
  const appConfig = useAppConfig();
  const location = await useLocation();
  const isUS = location.country === "US";

  if (!query.type?.length && !query.shared) {
    query.type = Object.values(EVENT_TYPES)
      .filter((type) => type !== EVENT_TYPES.SCOUT)
      .join(",");
  }

  if (query.startDate) query.startDate = getUnixTime(startOfDay(new Date(query.startDate).getTime() * 1000));

  if (query.endDate) query.endDate = getUnixTime(endOfDay(new Date(query.endDate).getTime() * 1000));

  if (query.status === EVENT_STATUS.UPCOMING) {
    // @ts-expect-error This override is required since on BE upcoming events have status upcoming live
    query.status = "upcoming_live";
  }

  query.include = "lineup";

  const getFormattedEventsList = (res: IResponseEvents, filterFn?: (event: IEvent) => boolean): { data: IEvent[]; total: number } => {
    const formattedData = res.data.map((i) => formatEvent(i, { isUS }));
    return {
      data: filterFn ? formattedData.filter(filterFn) : formattedData,
      total: res.meta.total,
    };
  };

  if (appConfig.useMockData) {
    return $fetch<IResponseEvents>("/mocks/data/events.json").then((res) =>
      getFormattedEventsList(res, query.status ? (item) => query.status === item.status : undefined)
    );
  }

  if (leagueId) return useAPI<IResponseEvents>(`/api/v1/leagues/${leagueId}/events`, { query }).then(getFormattedEventsList);

  if (teamId) {
    return useAPI<IResponseEvents>(`/api/v1/events/clubs/${teamId}`, { query }).then(getFormattedEventsList);
  }

  return useAPI<IResponseEvents>("/api/v1/events", { query }).then(getFormattedEventsList);
}

export async function getMbeEventsListByTeamId(
  teamId: string,
  query: Partial<
    Pick<IRequestEventParams, "startDate" | "endDate" | "offset" | "limit" | "id"> & {
      name: string;
    }
  >
): Promise<{ data: IMbeEvent[]; total: number }> {
  if (query.startDate) query.startDate = getUnixTime(startOfDay(new Date(query.startDate).getTime() * 1000));

  if (query.endDate) query.endDate = getUnixTime(endOfDay(new Date(query.endDate).getTime() * 1000));

  return useAPI<{ data: IResponseMBEEvent[] }>(`/api/v1/external/clubs/${teamId}/events`, { query }).then((res) => ({
    data: res.data.map((i) => formatMBEEvent(i)),
    total: res.data.length,
  }));
}

export async function getMbeEventById(mbeEventId: string): Promise<{ data: IMbeEvent; teamsMap: Record<string, ITeam> }> {
  const { $atlit } = useNuxtApp();

  return $atlit("/v1/external/events/{ID}", {
    path: { ID: mbeEventId },
    query: {
      relationships: "internalClubs",
    },
  }).then((res) => ({
    data: formatMBEEvent(res.data),
    teamsMap: Object.fromEntries(
      // @ts-expect-error TODO: Fix this
      Object.entries(res.data.relationships).map(([key, value]) => [key, formatTeam(value)])
    ),
  }));
}

export async function getReplayEvents(query: IRequestReplayEventsParams) {
  return useAPI<{ data: ReplayEvent[] }>("api/v1/external/replay/events", { query });
}

export const getCachedEventById = useMemoize(
  (eventId: string, options?: { accessToken?: string; customCallbacks?: { onAuthFailure?: () => void }; shouldSwapTeams?: boolean }) =>
    getEventById(eventId, options)
);

export async function getEventById(
  eventId: string,
  options?: { accessToken?: string; customCallbacks?: { onAuthFailure?: () => void }; shouldSwapTeams?: boolean }
): Promise<IEvent> {
  const baseURL = useRuntimeConfig().app.baseURL;
  const location = await useLocation();
  const isUS = location.country === "US";

  if (eventId === "demo")
    return $fetch<IResponseEvent>(`${baseURL}mocks/data/event/123/data.json`).then((res) =>
      formatEvent(res, { isUS, shouldSwapTeams: options?.shouldSwapTeams })
    );

  return useAPI<{ data: IResponseEvent }>(`/api/v1/events/${eventId}`, {
    ...(options?.accessToken && { headers: { "X-Shared-Authorization": `Bearer ${options.accessToken}` } }),
    query: {
      relationships: "leagues,club,favorite,highlight.favorite",
      with: "homeTeam,awayTeam",
    },
    customCallbacks: options?.customCallbacks,
  }).then((res) => formatEvent(res.data, { isUS, shouldSwapTeams: options?.shouldSwapTeams }));
}

export function sendEventForBreakdown(eventId: string, breakdownType: TBreakdownType) {
  return useAPI(`api/v1/events/${eventId}/breakdowns/${breakdownType}`, { method: "POST" });
}

export async function getVideo(
  videoId: IBasicVideo["id"],
  type: IBasicVideo["type"],
  options?: { accessToken?: string }
): Promise<IEvent | IClip | IGameHighlight | IAthleteHighlight> {
  const videoGetUrl = {
    event: () => getCachedEventById(videoId, options),
    clip: () => getUserClipById(videoId, options),
    highlight: () => getEventHighlightById(videoId, options),
    autohighlight: () => getEventHighlightById(videoId, options),
    "player-highlight": () => getEventAthleteHighlightById(videoId, options),
    "mbe-event": () => getCachedEventById(videoId, options), // Not used / not valid
  } as const;

  return videoGetUrl[type]();
}

function hashCode(str: string): string {
  let hash: number = 0;
  for (let i: number = 0, len = str.length; i < len; i++) {
    const chr: number = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return String(Math.abs(hash));
}

export async function createEvent(data: TCreateEventPayload): Promise<IEvent> {
  return useAPI<{ data: IResponseEvent }>("/api/v1/events", {
    method: "POST",
    body: data,
  }).then((res) => formatEvent(res.data));
}

export async function getVideoShareURL(
  video: IBasicVideo,
  options: {
    baseURL: string;
    accessToken?: string;
    includeStats?: boolean;
    /**
     * @description The expiration time in seconds.
     * @example 10 minutes = 600 seconds
     * @example 1 hour = 3600 seconds
     * @example 1 day = 86400 seconds
     * @example 1 week = 604800 seconds
     * @example 1 month = 2629746 seconds
     * @example 1 year = 31556952 seconds
     */
    expirationTime?: number | null;
    allowSaving?: boolean;
    sharedAngles?: string[];
  }
): Promise<string | undefined> {
  // Create hash from baseURL for uniq state param
  const baseURLHash = hashCode(options.baseURL);
  const sharedURL = useState(
    `shared-${video.type}-url-${video.id}-${baseURLHash}-${options.includeStats}-${options.expirationTime}-${options.allowSaving}-${options.sharedAngles}`,
    () => ""
  );

  // Return the url from cache if such
  if (sharedURL.value) return sharedURL.value;

  const appConfig = useAppConfig();
  let accessToken = options?.accessToken;

  if (!accessToken) {
    const videoShareUrl = {
      event: (id: string) => `/api/v1/events/${id}/share`,
      clip: (id: string) => `/api/v1/clips/${id}/share`,
      highlight: (id: string) => `/api/v1/events/highlights/${id}/share`,
      autohighlight: (id: string) => `/api/v1/events/highlights/${id}/share`,
      "player-highlight": (id: string) => `/api/v1/player-highlights/${id}/share`,
      "mbe-event": (id: string) => `/api/v1/external/events/${id}/share`, // Not used / not valid
    } as const;

    accessToken = await useAPI<{ data: { token: string } }>(videoShareUrl[video.type](video.id), {
      method: "POST",
      body:
        video.type === "event"
          ? {
              stream: "hd",
              includeStats: options.includeStats ?? undefined,
              expirationTime: options.expirationTime ?? undefined,
              clubIdsShared: options.allowSaving ?? undefined,
              streams: options.sharedAngles?.length ? options.sharedAngles : undefined,
            }
          : undefined,
    }).then((res) => res.data.token);
  }

  // TODO: Move accessToken into the hash params
  const url = options.baseURL.includes("?") ? `${options.baseURL}&accessToken=${accessToken}` : `${options.baseURL}?accessToken=${accessToken}`;

  if (appConfig.useMockData) return Promise.resolve(url);

  const fallbackPoster = `${window.location.origin}/images/no_poster_light.jpg`;

  const title = `Watch "${video.title}" at Pixellot!`;
  const description = `Shared ${video.type} from Pixellot.`;
  const poster = !video.poster ? fallbackPoster : video.poster;

  const branchIO = useBranchIO();
  const linkData = {
    feature: `share-${video.type}`,
    data: {
      // general
      title: title,
      description: description,
      image: poster,
      eventId: video.id,
      token: accessToken,
      action: "shared_video",
      video_type: video.type,
      // branchIO specific
      $og_title: title,
      $og_description: description,
      $og_image_url: poster,
      $og_url: url,
      $desktop_url: url,
      $ios_url: url,
      $ipad_url: url,
      $android_url: url,
      $windows_phone_url: url,
      $blackberry_url: url,
      $fire_url: url,
      $twitter_title: title,
      $twitter_description: description,
      $twitter_image_url: poster,
      // meta tags
      $custom_meta_tags: JSON.stringify({
        title: title,
        description: description,
        canonical: url,
        url: url,
        ogUrl: url,
        oEmbedImage: poster,
        ogTitle: title,
        ogDescription: description,
        ogImage: poster,
        twitterTitle: title,
        twitterDescription: description,
        twitterImage: poster,
        twitterCard: "summary_large_image",
      }),
    },
  };

  sharedURL.value = await branchIO.link(linkData);

  return sharedURL.value;
}

export async function exportShareDownloadableFiles(eventId: string, payload: TExportAnglesPayload, options?: { accessToken?: string }): Promise<void> {
  return useAPI(`/api/v1/events/${eventId}/export-angles`, {
    method: "POST",
    body: payload,
    ...(options?.accessToken && { headers: { "X-Shared-Authorization": `Bearer ${options.accessToken}` } }),
  });
}

export async function requestExport(eventId: string, options?: { stream?: IPixellotPlayerSourceType }): Promise<void> {
  const body = { mimeType: "video/mp4", stream: options?.stream };
  return useAPI(`/api/v1/events/${eventId}/export`, { method: "POST", body });
}

export async function deleteEvent(eventId: string): Promise<void> {
  return useAPI(`/api/v1/events/${eventId}`, { method: "DELETE" }).then(() => getCachedEventsList.clear());
}

export function getEditLineupUrl(
  eventId: string,
  options?: {
    backUrl?: string;
  }
): Promise<string> {
  return useAPI<{ data: { link: string; expiresAt: number } }>(`/api/v1/events/${eventId}/lineups/auth`, {
    query: { backUrl: options?.backUrl },
  }).then((res) => res.data.link);
}

export function getAdvancedEditorUrl(
  eventId: string,
  options?: {
    backUrl?: string;
  }
): Promise<string> {
  return useAPI<{ data: { link: string; expiresAt: number } }>(`/api/v1/events/${eventId}/editor/auth`, {
    query: { backUrl: options?.backUrl },
  }).then((res) => res.data.link);
}

export function getClippingQueueUrl(
  eventId: string,
  options?: {
    backUrl?: string;
  }
): Promise<string> {
  return useAPI<{ data: { link: string; expiresAt: number } }>(`/api/v1/events/${eventId}/clipping-queue/auth`, {
    query: { backUrl: options?.backUrl },
  }).then((res) => res.data.link);
}

export function getMatchOverviewUrl(eventId: string): Promise<string> {
  return useAPI<{ data: { link: string; expiresAt: number } }>(`/api/v1/events/${eventId}/playlists/auth`).then((res) => res.data.link);
}

export function getMatchChartsUrl(eventId: string): Promise<string> {
  return useAPI<{ data: { link: string; expiresAt: number } }>(`/api/v1/events/${eventId}/charts/auth`).then((res) => res.data.link);
}

export type TUpdateEventPayload = components["schemas"]["UpdateEventAttr"];

export async function updateEvent(eventId: string, data: TUpdateEventPayload): Promise<void> {
  return useAPI<{ data: IResponseEvent }>(`/api/v1/events/${eventId}`, {
    method: "PATCH",
    body: data,
  }).then(() => {
    getCachedEventById.clear();
    getCachedEventGameStatsByIdV1.clear();
    getCachedEventGameStatsByIdV3.clear();
    getCachedEventAthletesStatsById.clear();
  });
}

export async function createSharedExternalEvent(data: { clubId: string }, options: { accessToken: string }): Promise<ISharedExternalEvent> {
  return useAPI<{ data: IResponseSharedExternalEvent }>("/api/v1/events/shared-events", {
    method: "POST",
    body: data,
    ...(options?.accessToken && { headers: { "X-Shared-Authorization": `Bearer ${options.accessToken}` } }),
  }).then((res) => formatSharedExternalEvent(res.data));
}

export async function getEventClipsAngles(eventId: string, options?: { accessToken?: string }): Promise<ISportAngle[]> {
  return useAPI<{ data: TResponseSportAngle[] }>(`/api/v1/events/${eventId}/clips/angles`, {
    ...(options?.accessToken && { headers: { "X-Shared-Authorization": `Bearer ${options.accessToken}` } }),
  }).then((res) => res.data.map(formatSportAngle));
}
